/**
 * Attendance API service - check-in/check-out for gardeners, team overview for companies
 */

import { apiGet, apiPost } from './api';

export type AttendanceStatus = 'not_checked_in' | 'checked_in' | 'checked_out' | 'not_applicable';

export type MyAttendanceStatus = {
	can_check_in: boolean;
	can_check_out: boolean;
	status: AttendanceStatus;
	today: {
		id?: number;
		date: string;
		check_in_at: string | null;
		check_out_at: string | null;
	} | null;
	message?: string | null;
	company_id?: number | null;
};

export type TeamMemberAttendance = {
	id: number;
	name: string;
	email: string;
	company_name: string | null;
	check_in_at: string | null;
	check_out_at: string | null;
	status: 'not_checked_in' | 'checked_in' | 'checked_out';
};

export type TeamAttendanceResponse = {
	team: TeamMemberAttendance[];
	date: string;
	checked_in_count: number;
	total_count: number;
	message?: string | null;
};

export type AttendanceHistoryEntry = {
	id: number;
	date: string;
	check_in_at: string | null;
	check_out_at: string | null;
	status: 'not_checked_in' | 'checked_in' | 'checked_out';
};

export type AttendanceHistoryResponse = {
	gardener_id: number;
	from: string;
	to: string;
	entries: AttendanceHistoryEntry[];
};

export async function getMyAttendanceStatus(userId: string): Promise<MyAttendanceStatus> {
	return apiGet<MyAttendanceStatus>(`/attendance/me?user_id=${encodeURIComponent(userId)}`);
}

export async function checkIn(userId: string): Promise<{ status: string; check_in_at: string }> {
	return apiPost<{ status: string; check_in_at: string }>('/attendance/check-in', { user_id: userId });
}

export async function checkOut(userId: string): Promise<{ status: string; check_out_at: string }> {
	return apiPost<{ status: string; check_out_at: string }>('/attendance/check-out', { user_id: userId });
}

export async function getTeamAttendance(
	userId: string,
	date?: string
): Promise<TeamAttendanceResponse> {
	const params = new URLSearchParams({ user_id: userId });
	if (date) params.set('date', date);
	return apiGet<TeamAttendanceResponse>(`/attendance/team?${params.toString()}`);
}

export async function getGardenerAttendanceHistory(
	gardenerId: number,
	from?: string,
	to?: string,
	companyId?: number | null
): Promise<AttendanceHistoryResponse> {
	const params = new URLSearchParams({ gardener_id: String(gardenerId) });
	if (from) params.set('from', from);
	if (to) params.set('to', to);
	if (companyId != null && Number.isFinite(companyId) && companyId > 0) {
		params.set('company_id', String(companyId));
	}
	return apiGet<AttendanceHistoryResponse>(`/attendance/history?${params.toString()}`);
}
